import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MatchMode } from '@/lib/inventory-api';

interface Props {
  /** Current value (partial text for filtering). */
  value: string;
  /** Called with the value. */
  onChange: (v: string) => void;
  /** Match mode determines search behavior. */
  matchMode: MatchMode;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Free text filter input. All match modes now support partial text:
 * - 'contains': finds "HF" anywhere in the text (e.g., "WHF 123", "HF 456")
 * - 'startswith': finds text starting with "HF" (e.g., "HF 456", "HFL 789")
 * - 'similar': like startswith but ignores spaces/punctuation
 */
export function FilterInput({
  value, onChange, matchMode,
  placeholder = 'Search…', label, className, disabled,
}: Props) {
  const [draft, setDraft] = React.useState(value);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync draft with external value
  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleChange = (text: string) => {
    setDraft(text);
    
    // Debounce the onChange to avoid too many API calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange(text.trim());
    }, 400);
  };

  const clear = () => {
    setDraft('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange('');
  };

  const modeHint = matchMode === 'startswith' 
    ? 'starts with' 
    : matchMode === 'similar' 
      ? 'similar to' 
      : 'contains';

  return (
    <div className={cn('relative', className)}>
      {label && (
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
          <span className="ml-1 font-normal normal-case text-copper">({modeHint})</span>
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={draft}
          disabled={disabled}
          placeholder={`Type to filter (${modeHint})…`}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Immediately commit on Enter
              e.preventDefault();
              if (debounceRef.current) clearTimeout(debounceRef.current);
              onChange(draft.trim());
            }
          }}
          className={cn(
            'h-10 w-full rounded-md border border-input bg-background px-3 pr-8 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring',
            disabled && 'opacity-50',
          )}
        />
        {draft && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); clear(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-ink"
            aria-label="Clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
